/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { UserRoles } from '@bb/users';
import { Feature } from '@bb/automaton';
import { AuthContext } from 'contexts';
import { Tabs, TabPane, message } from 'components';
import {
  featureEnabled,
  hasValidStripeMlsProductId,
  isAuthForRole,
} from 'utils';
import { getUnsubscribeId } from 'api/NotificationPreferenceController';
import { useHistory } from 'react-router-dom';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types';
import FormContext from '../FormContext';
import { TabKey } from '../types';

/**
 * PageTabs are used on four different pages:
 * The Manage Users page in the Admin menu (isEdit). This is the assumed default if all others are undefined.
 * The New User page for creating a user (isNew).
 * The Account Settings for an exisitng user (isProfile).
 * The Incoming Transfer page for viewing an incoming transfer (isIncomingTransfer).
 */
const PageTabs = () => {
  const { answeringPortalConfig, auth, hasFeature } = useContext(AuthContext);
  const {
    isNew,
    isProfile,
    isIncomingTransfer,
    userId,
    loading,
    activeTab,
    setActiveTab,
    isAnsweringOrMlsStaff,
    isDeleted,
    userState,
  } = useContext(FormContext);
  const history = useHistory();

  const { branch, role, teamAccess } = userState;

  const isSupraSubscriptionActive = useMemo(() => {
    if (!userState.supraSubscription) return false;

    if (userState.supraSubscription?.status === 'subscribed') {
      return true;
    }
    return false;
  }, [userState]);

  const hasSubscription = !!auth?.supraSubscription;
  const isDeactivatedOrSuspended =
    auth?.supraSubscription?.status ===
      SupraSystemSubscriptionStatus.deactivated ||
    auth?.supraSubscription?.status === SupraSystemSubscriptionStatus.suspended;

  const shouldShowSupraTab =
    hasFeature(Feature.SUPRA_MVP) &&
    isSupraSubscriptionActive &&
    (!hasSubscription || !isDeactivatedOrSuspended);

  if (loading || isDeleted || isAnsweringOrMlsStaff) return <></>;

  /** Whether the user is authorized to view/modify billing details for subscriptions. */
  let hasAccessToBilling = false;

  // Never show billing on the create new user or incoming transfer user page.
  if (!isNew && !isIncomingTransfer && userId) {
    // Only admins of the this team/branch location or the user themself are allowed to view subscription billing details.
    // Co-opting this method and sending in the reps field to check if an isProfile user (where auth.id == userId) is viewing
    // this tab logged in as themself. Not what the reps field was intended for but, hey, it works.
    hasAccessToBilling =
      isAuthForRole({
        auth,
        location: branch,
        teams: teamAccess || [],
        reps: [{ id: userId }],
      }) &&
      auth.roles[auth.role] >= auth.roles[role] &&
      !featureEnabled('subscriptionHubFeature');
  }

  const redirectToNotifications = async (idToConvert: string) => {
    try {
      const unsubscribeId = await getUnsubscribeId(idToConvert);
      if (unsubscribeId) {
        history.push(`/unsubscribe/${unsubscribeId}`);
      }
    } catch (err) {
      message.error('Error redirecting to user notifications page.');
    }
  };

  const onTabPress = async (key: TabKey) => {
    if (key === 'NOTIFICATION_PREFERENCES') {
      if (userId) {
        await redirectToNotifications(userId);
      }
    } else {
      setActiveTab(key);
    }
  };
  return (
    <StyledTabs
      activeKey={activeTab}
      onChange={onTabPress}
      data-cy="page-tab-list"
    >
      <TabPane tab={<span data-cy="profile-tab">Profile</span>} key="PROFILE" />
      {shouldShowSupraTab && (
        <TabPane
          tab={<span data-cy="supra-subscription-tab">Supra Subscription</span>}
          key="SUPRA_SUBSCRIPTION"
        />
      )}

      <TabPane
        tab={
          <span data-cy="front-desk-instructions-tab">
            Front Desk Instructions
          </span>
        }
        key="FRONT_DESK_INSTRUCTIONS"
      />
      <TabPane
        tab={<span data-cy="listing-settings-tab">Listing Settings</span>}
        key="LISTING_SETTINGS"
      />
      {hasAccessToBilling &&
        (answeringPortalConfig?.useLiveAppointmentDesk ||
          hasValidStripeMlsProductId(answeringPortalConfig?.mlsProductId)) && (
          <TabPane
            tab={
              <span data-cy="live-concierge-service-tab">
                Live Concierge Service
              </span>
            }
            key="LIVE_CONCIERGE_SERVICE"
            disabled={isNew}
          />
        )}
      {!(isProfile || isIncomingTransfer) && (
        <TabPane
          tab={
            <span data-cy="notification-preferences-tab">
              Notification Preferences
            </span>
          }
          key="NOTIFICATION_PREFERENCES"
          disabled={isNew}
        />
      )}
      {hasAccessToBilling && !auth.isAssumedNotBB && (
        <TabPane
          tab={<span data-cy="billing-tab">User Billing</span>}
          key="BILLING"
          disabled={isNew || isIncomingTransfer}
        />
      )}
      {role === UserRoles.Owner && auth.role === UserRoles.Owner && (
        <TabPane
          tab={<span data-cy="permissions-tab">Permissions</span>}
          key="PERMISSIONS"
        />
      )}
    </StyledTabs>
  );
};

export default PageTabs;

const StyledTabs = styled(Tabs).attrs({
  tabBarStyle: { marginBottom: 0 },
})`
  padding: 0 24px;
  background: white;
`;
